import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PROJECT_CONFIG from "../../config/projectConfig";
import {
  login,
  selectAuthError,
  selectAuthLoading,
  selectIsAuthenticated,
} from "../../redux/authReducer";
import { loginSchema } from "../../types/schemas";
import AlertBanner from "../shared/AlertBanner";

/** Dramatic ocean - readable brand overlay, vivid teal water still visible */
const PANEL_IMAGE = "/images/ocean-waves.jpg";

const Login = () => {
  const [activeTab, setActiveTab] = useState("login");
  const tabsId = useId();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  const from = location.state?.from?.pathname || "/dashboard";
  const name = PROJECT_CONFIG.name;
  const fullName = PROJECT_CONFIG.fullName;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(login(data)).unwrap();

      if (result) {
        toast.success("Login successful! Redirecting to dashboard...");
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 500);
      }
    } catch (err) {
      toast.error(err || "Login failed");
    }
  };

  const tabs = [
    { id: "login", label: "Log in" },
    { id: "create", label: "Apply" },
    { id: "reset", label: "Reset" },
  ];

  const panelTitle = {
    login: "Welcome back",
    create: "Join the Cadre",
    reset: "Reset password",
  };

  const panelSubtitle = {
    login: `Sign in to your ${name} account to continue.`,
    create: "Apply to serve as an independent scientific expert.",
    reset: "We'll email you a link to choose a new password.",
  };

  const handleTabKeyDown = (event, index) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const nextIndex =
      event.key === "ArrowRight"
        ? (index + 1) % tabs.length
        : (index - 1 + tabs.length) % tabs.length;
    setActiveTab(tabs[nextIndex].id);
    document.getElementById(`${tabsId}-tab-${tabs[nextIndex].id}`)?.focus();
  };

  const inputClass =
    "w-full border border-srp-ink/15 bg-white px-4 py-3.5 text-base text-srp-ink shadow-sm placeholder:text-srp-ink/40 transition hover:border-custom-blue/40 focus:border-custom-blue focus:outline-none focus:ring-2 focus:ring-custom-blue/25";

  return (
    <div className="relative min-h-[calc(100svh-4.5rem)] overflow-hidden bg-srp-ink text-srp-ink">
      {/* Full-bleed atmosphere behind both columns */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={PANEL_IMAGE}
          alt=""
          className="h-full w-full object-cover motion-safe:animate-hero-zoom"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-srp-ink via-srp-ink/90 to-custom-blue/75 lg:via-srp-ink/85 lg:to-custom-blue/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-srp-ink/85 via-srp-ink/25 to-srp-ink/45" />
      </div>

      <div className="relative mx-auto grid min-h-[calc(100svh-4.5rem)] max-w-7xl lg:grid-cols-12">
        {/* Brand copy */}
        <aside
          className="flex flex-col justify-center px-6 py-12 text-white sm:px-10 lg:col-span-5 lg:px-12 lg:py-16 xl:px-16"
          aria-labelledby="login-brand-heading"
        >
          <p
            id="login-brand-heading"
            className="text-5xl font-bold tracking-tight sm:text-6xl motion-safe:animate-hero-rise"
          >
            {name}
          </p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-custom-cyan-bright sm:text-sm motion-safe:animate-hero-rise">
            {fullName}
          </p>
          <p className="mt-8 max-w-md text-lg leading-relaxed text-white/95 motion-safe:animate-hero-rise-delay sm:text-xl">
            Independent science for flood map resolution. Manage panels, review
            applications, and keep the process moving.
          </p>

          <ul className="mt-10 hidden space-y-4 text-sm text-white/85 motion-safe:animate-hero-rise-delay-2 lg:block">
            <li className="flex gap-3">
              <span
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-custom-cyan-bright"
                aria-hidden="true"
              />
              Neutral scientific review for FEMA and communities
            </li>
            <li className="flex gap-3">
              <span
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-custom-cyan-bright"
                aria-hidden="true"
              />
              Expert cadre across coastal, hydrologic, and mapping fields
            </li>
            <li className="flex gap-3">
              <span
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-custom-cyan-bright"
                aria-hidden="true"
              />
              Clear recommendations when consultation reaches an impasse
            </li>
          </ul>
        </aside>

        {/* Form surface */}
        <section
          className="flex items-center px-4 pb-12 sm:px-8 lg:col-span-7 lg:px-10 lg:py-16 xl:px-14"
          aria-labelledby="login-page-heading"
        >
          <div className="mx-auto w-full max-w-lg motion-safe:animate-hero-rise-delay">
            <div className="border border-white/60 bg-white/95 p-6 shadow-[0_24px_80px_-20px_rgba(10,31,51,0.45)] backdrop-blur-sm sm:p-9">
              <div
                className="flex rounded-sm bg-srp-foam p-1"
                role="tablist"
                aria-label="Account"
              >
                {tabs.map((tab, index) => {
                  const selected = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      id={`${tabsId}-tab-${tab.id}`}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      aria-controls={`${tabsId}-panel-${tab.id}`}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => setActiveTab(tab.id)}
                      onKeyDown={(e) => handleTabKeyDown(e, index)}
                      className={`flex-1 px-3 py-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue focus-visible:ring-offset-2 sm:text-base ${
                        selected
                          ? "bg-custom-blue text-white shadow-sm"
                          : "text-srp-ink/65 hover:text-custom-blue"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <h1
                id="login-page-heading"
                className="mt-8 text-3xl font-bold text-custom-blue sm:text-4xl"
              >
                {panelTitle[activeTab]}
              </h1>
              <p className="mt-2 text-base text-srp-ink/75">
                {panelSubtitle[activeTab]}
              </p>

              {activeTab === "login" && (
                <div
                  id={`${tabsId}-panel-login`}
                  role="tabpanel"
                  aria-labelledby={`${tabsId}-tab-login`}
                  className="mt-8"
                >
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {error && <AlertBanner type="error" message={error} />}

                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-semibold text-custom-blue"
                      >
                        Email{" "}
                        <span className="text-red-700" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only"> (required)</span>
                      </label>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="email"
                            id="email"
                            autoComplete="email"
                            aria-invalid={errors.email ? true : undefined}
                            aria-describedby={
                              errors.email ? "login-email-error" : undefined
                            }
                            placeholder="you@example.com"
                            className={inputClass}
                          />
                        )}
                      />
                      {errors.email && (
                        <p
                          id="login-email-error"
                          className="mt-1.5 text-sm text-red-700"
                          role="alert"
                        >
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="mb-2 flex items-baseline justify-between gap-3">
                        <label
                          htmlFor="password"
                          className="block text-sm font-semibold text-custom-blue"
                        >
                          Password{" "}
                          <span className="text-red-700" aria-hidden="true">
                            *
                          </span>
                          <span className="sr-only"> (required)</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setActiveTab("reset")}
                          className="text-sm font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            aria-invalid={errors.password ? true : undefined}
                            aria-describedby={
                              errors.password
                                ? "login-password-error"
                                : undefined
                            }
                            placeholder="Enter your password"
                            className={inputClass}
                          />
                        )}
                      />
                      {errors.password && (
                        <p
                          id="login-password-error"
                          className="mt-1.5 text-sm text-red-700"
                          role="alert"
                        >
                          {errors.password.message}
                        </p>
                      )}
                    </div>

                    <div className="pt-1">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-custom-blue px-4 py-3.5 text-base font-semibold text-white transition hover:bg-custom-light-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {loading ? "Signing in..." : "Sign in"}
                      </button>
                    </div>
                  </form>

                  <p className="mt-8 border-t border-srp-ink/10 pt-6 text-sm text-srp-ink/70">
                    New to {name}?{" "}
                    <button
                      type="button"
                      onClick={() => setActiveTab("create")}
                      className="font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
                    >
                      Apply to the Cadre
                    </button>
                  </p>
                </div>
              )}

              {activeTab === "create" && (
                <div
                  id={`${tabsId}-panel-create`}
                  role="tabpanel"
                  aria-labelledby={`${tabsId}-tab-create`}
                  className="mt-8"
                >
                  <p className="leading-relaxed text-srp-ink">
                    Submit your credentials and experience to join the expert
                    cadre that reviews conflicting flood hazard data for FEMA
                    and communities.
                  </p>
                  <Link
                    to="/cadre-application"
                    className="mt-8 inline-flex w-full items-center justify-center bg-custom-blue px-4 py-3.5 text-base font-semibold text-white transition hover:bg-custom-light-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
                  >
                    Go to application
                  </Link>
                  <p className="mt-6 text-sm text-srp-ink/70">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setActiveTab("login")}
                      className="font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              )}

              {activeTab === "reset" && (
                <div
                  id={`${tabsId}-panel-reset`}
                  role="tabpanel"
                  aria-labelledby={`${tabsId}-tab-reset`}
                  className="mt-8"
                >
                  <form className="space-y-5">
                    <div>
                      <label
                        htmlFor="reset-email"
                        className="mb-2 block text-sm font-semibold text-custom-blue"
                      >
                        Email address
                      </label>
                      <input
                        type="email"
                        id="reset-email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        className={inputClass}
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-custom-blue px-4 py-3.5 text-base font-semibold text-white transition hover:bg-custom-light-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue focus-visible:ring-offset-2"
                    >
                      Send reset instructions
                    </button>
                  </form>
                  <p className="mt-6 text-sm text-srp-ink/70">
                    Remembered your password?{" "}
                    <button
                      type="button"
                      onClick={() => setActiveTab("login")}
                      className="font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
                    >
                      Back to sign in
                    </button>
                  </p>
                </div>
              )}
            </div>

            <p className="mt-6 text-center text-sm font-medium text-white">
              Need help?{" "}
              <Link
                to="/contact"
                className="font-semibold text-custom-cyan-bright underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Contact the {name} team
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
